import React, { useState } from 'react';
import AuthLayout from './AuthLayout';
import { supabase } from '../../lib/supabase';
import { Mail, Lock, LogIn, Github, Twitter, Facebook, Instagram } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const LoginPage = ({ isDarkMode, setIsDarkMode }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const { error: loginError } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (loginError) {
            console.error('Login error:', loginError);
            if (loginError.status === 400 || loginError.message?.includes('Invalid login credentials')) {
                setError('Invalid email or password. Please try again.');
            } else {
                setError(loginError.message);
            }
            setLoading(false);
        } else {
            navigate('/dashboard');
        }
    };

    const handleSocialLogin = async (provider) => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo: `${window.location.origin}/dashboard`,
            },
        });
        if (error) setError(error.message);
    };

    return (
        <AuthLayout
            title="Welcome Back"
            subtitle="Enter the sacred rhythm"
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
        >
            <form onSubmit={handleLogin} className="space-y-6">
                <div>
                    <label className={`block text-xs font-black uppercase tracking-widest mb-2 ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        Email Address
                    </label>
                    <div className="relative">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 rounded-2xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                                ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                                : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                                }`}
                            placeholder="name@example.com"
                        />
                        <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`} />
                    </div>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className={`block text-xs font-black uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                            Password
                        </label>
                        <Link to="/forgot-password" title="Forgot Password" className={`text-xs font-black uppercase tracking-widest hover:text-blue-500 transition-colors ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>
                            Forgot?
                        </Link>
                    </div>
                    <div className="relative">
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`w-full pl-12 pr-4 py-4 rounded-2xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                                ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                                : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                                }`}
                            placeholder="••••••••"
                        />
                        <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`} />
                    </div>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold p-4 rounded-xl text-center">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-mystic-purple hover:scale-[1.02] active:scale-[0.98] text-white font-black py-4 rounded-2xl shadow-lg shadow-mystic-purple/20 flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:hover:scale-100"
                >
                    <LogIn className="w-5 h-5" />
                    <span>{loading ? 'Entering...' : 'Enter Sanctuary'}</span>
                </button>
            </form>

            <div className="mt-6">
                <div className="relative flex items-center mb-4">
                    <div className="flex-grow border-t border-white/[0.05]"></div>
                    <span className="flex-shrink mx-3 text-gray-600 text-[10px] font-bold uppercase tracking-widest">Or login with</span>
                    <div className="flex-grow border-t border-white/[0.05]"></div>
                </div>

                <div className="grid grid-cols-4 gap-3">
                    <button onClick={() => handleSocialLogin('google')} className={`flex items-center justify-center p-3 rounded-xl border transition-all hover:scale-110 ${isDarkMode ? 'bg-cosmic-800 border-white/10 hover:bg-white/5' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
                    </button>
                    <button onClick={() => handleSocialLogin('facebook')} className={`flex items-center justify-center p-3 rounded-xl border transition-all hover:scale-110 ${isDarkMode ? 'bg-cosmic-800 border-white/10 hover:bg-white/5' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                        <Facebook className="w-5 h-5 text-[#1877F2]" fill="#1877F2" />
                    </button>
                    <button onClick={() => handleSocialLogin('twitter')} className={`flex items-center justify-center p-3 rounded-xl border transition-all hover:scale-110 ${isDarkMode ? 'bg-cosmic-800 border-white/10 hover:bg-white/5' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                        <Twitter className="w-5 h-5 text-[#1DA1F2]" fill="#1DA1F2" />
                    </button>
                    <button onClick={() => handleSocialLogin('instagram')} className={`flex items-center justify-center p-3 rounded-xl border transition-all hover:scale-110 ${isDarkMode ? 'bg-cosmic-800 border-white/10 hover:bg-white/5' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                        <Instagram className="w-5 h-5 text-[#E4405F]" />
                    </button>
                </div>
            </div>

            <div className="mt-8 text-center">
                <p className="text-gray-500 text-xs font-medium">
                    New here?{' '}
                    <Link to="/signup" className="text-blue-500 hover:text-blue-400 font-bold transition-colors">
                        Create an account
                    </Link>
                </p>
            </div>
        </AuthLayout>
    );
};

export default LoginPage;
